'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'

import { UpdateUserProfileRequest, useUpdateUserProfileMutation } from '@/src/api/rtkQuery/profileApi'
import { TITLES } from '@/src/constants/travelers'
import { ProfileData } from '@/src/types/profile'
import useFormatDate from '@/src/hooks/date/useFormatDate'

import { EditProfile, EditProfileFormValues, EditProfileOption } from './editProfile'

const PROFILE_TITLE_VALUES = [...TITLES.ADT] as const

interface EditProfileWrapperProps {
    initialProfile: ProfileData
    onCancel: () => void
    onSave: () => void
    /** Hide the in-form heading when the surface already shows the title (e.g. mobile header). */
    hideHeading?: boolean
}

function buildInitialFormValues(
    profile: ProfileData,
    formatDate: (date: string, format?: string) => string
): EditProfileFormValues {
    const titleRaw = profile.title.replace(/\.$/, '')
    const dateOfBirthInput = profile.dateOfBirth ? formatDate(profile.dateOfBirth, 'YYYY-MM-DD') : ''

    return {
        title: titleRaw,
        firstName: profile.firstName,
        lastName: profile.lastName,
        // API enum is uppercase (MALE / FEMALE / OTHER · SINGLE / MARRIED).
        gender: (profile.gender || '').toUpperCase(),
        maritalStatus: (profile.maritalStatus || '').toUpperCase(),
        dateOfBirth: dateOfBirthInput,
        marketingSubscription: false
    }
}

/** Converts a yyyy-mm-dd date input to the API's date-time format, or null when empty. */
function toApiDateTime(dateInput: string): string | null {
    return dateInput ? `${dateInput}T00:00:00` : null
}

const EditProfileWrapper: React.FC<EditProfileWrapperProps> = ({ initialProfile, onCancel, onSave, hideHeading }) => {
    const { formatDate } = useFormatDate()
    const t = useTranslations('account.profile')
    const tTitles = useTranslations('common.titles')
    const [updateUserProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation()
    const initialValues = useMemo(() => buildInitialFormValues(initialProfile, formatDate), [initialProfile, formatDate])
    const [formValues, setFormValues] = useState<EditProfileFormValues>(initialValues)
    const [saveError, setSaveError] = useState<string | null>(null)

    const titleOptions: EditProfileOption[] = useMemo(
        () =>
            PROFILE_TITLE_VALUES.map(value => ({
                value,
                label: tTitles(value.toLowerCase())
            })),
        [tTitles]
    )

    const genderOptions: EditProfileOption[] = useMemo(
        () => [
            { value: 'MALE', label: t('genderMale') },
            { value: 'FEMALE', label: t('genderFemale') },
            { value: 'OTHER', label: t('genderOther') }
        ],
        [t]
    )

    const maritalStatusOptions: EditProfileOption[] = useMemo(
        () => [
            { value: 'SINGLE', label: t('maritalSingle') },
            { value: 'MARRIED', label: t('maritalMarried') }
        ],
        [t]
    )

    const handleChange = useCallback((patch: Partial<EditProfileFormValues>) => {
        setFormValues(prev => ({ ...prev, ...patch }))
        setSaveError(null)
    }, [])

    const handleSave = useCallback(async () => {
        try {
            setSaveError(null)
            const payload: UpdateUserProfileRequest = {
                marketingSubscription: formValues.marketingSubscription ? 1 : 0,
                personalData: {
                    title: formValues.title,
                    firstName: formValues.firstName,
                    lastName: formValues.lastName,
                    gender: formValues.gender || undefined,
                    maritalStatus: formValues.maritalStatus || undefined,
                    dateOfBirth: toApiDateTime(formValues.dateOfBirth)
                }
            }
            await updateUserProfile(payload).unwrap()
            onSave()
        } catch {
            setSaveError(t('saveFailed'))
        }
    }, [formValues, updateUserProfile, onSave, t])

    return (
        <EditProfile
            values={formValues}
            t={t}
            titleOptions={titleOptions}
            genderOptions={genderOptions}
            maritalStatusOptions={maritalStatusOptions}
            isSaving={isSaving}
            saveError={saveError}
            hideHeading={hideHeading}
            onChange={handleChange}
            onCancel={onCancel}
            onSave={handleSave}
        />
    )
}

export { EditProfileWrapper }
export type { EditProfileWrapperProps }
export default EditProfileWrapper
